import React from 'react';
import Draggable from 'react-draggable';
import { FiRefreshCw } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { TbArrowsMaximize, TbArrowsMinimize } from 'react-icons/tb';

import {
  readSurveyDataViewerInitialOffset,
  useDraggableModalPosition,
} from 'hooks/useDraggableModalPosition';

import HomePage from './pages/HomePage/HomePage';
import { useColorPickerDragLock } from './colorPickerDragLock';
import { SURVEY_DATA_VIEWER_MODAL_TITLE } from './constants';

interface SurveyDataManagerPanelProps {
  readonly isModalMinimized: boolean;
  readonly isRefreshPending: boolean;
  readonly refreshRequestId: number;
  readonly zIndex?: number;
  onClose: () => void;
  onRefresh: () => void;
  onRefreshSettled: () => void;
  onToggleMinimized: () => void;
}

function SurveyDataManagerPanel({
  isModalMinimized,
  isRefreshPending,
  refreshRequestId,
  zIndex,
  onClose,
  onRefresh,
  onRefreshSettled,
  onToggleMinimized,
}: SurveyDataManagerPanelProps): JSX.Element {
  const { isColorPickerOpen } = useColorPickerDragLock();
  const { position, bounds, modalRef, onDragStop } = useDraggableModalPosition({
    initialOffset: readSurveyDataViewerInitialOffset,
  });

  const handleRefreshKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRefresh();
    }
  };

  const handleModalControlKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggleMinimized();
    }
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <Draggable
      nodeRef={modalRef}
      enableUserSelectHack={false}
      handle=".survey-data-viewer-modal__drag-handle"
      position={position}
      onStop={onDragStop}
      bounds={bounds}
      disabled={isColorPickerOpen}
    >
      <div
        ref={modalRef}
        className={`react-modal top-left survey-data-viewer-modal${
          isModalMinimized ? ' minimized' : ''
        }`}
        data-testid="survey-data-viewer-modal"
        style={zIndex != null ? { zIndex } : undefined}
      >
        <div className="modal-header survey-data-viewer-modal__drag-handle">
          <div className="modal-title-container">
            <div className="modal-title-in-container">
              {SURVEY_DATA_VIEWER_MODAL_TITLE}
            </div>
            <div
              className={
                isRefreshPending
                  ? 'modal-refresh-button rotating'
                  : 'modal-refresh-button'
              }
              onClick={onRefresh}
              role="button"
              tabIndex={0}
              aria-label="Refresh Survey Data Viewer"
              onKeyDown={handleRefreshKeyDown}
            >
              <FiRefreshCw />
            </div>
          </div>
          <div className="modal-header-buttons">
            <div
              className="modal-control"
              onClick={onToggleMinimized}
              role="button"
              tabIndex={0}
              aria-label={
                isModalMinimized
                  ? 'Maximize Survey Data Viewer'
                  : 'Minimize Survey Data Viewer'
              }
              onKeyDown={handleModalControlKeyDown}
            >
              {isModalMinimized ? <TbArrowsMaximize /> : <TbArrowsMinimize />}
            </div>
            <div
              className="modal-close"
              onClick={onClose}
              role="button"
              tabIndex={0}
              aria-label="Close Survey Data Viewer"
              onKeyDown={handleCloseKeyDown}
            >
              <MdClose />
            </div>
          </div>
        </div>
        <div className="modal-content survey-data-viewer-modal__body">
          <HomePage
            refreshData={isRefreshPending}
            refreshRequestId={refreshRequestId}
            onRefreshSettled={onRefreshSettled}
          />
        </div>
      </div>
    </Draggable>
  );
}

export default SurveyDataManagerPanel;
